import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  QrCode,
  MoreVertical,
  LayoutGrid,
  FileText,
  TrendingUp,
  Ticket,
} from "lucide-react";
import NavItems from "./NavItem";

interface NavigationProps {
  setIsCheckBookingOpen: (open: boolean) => void;
}

const LOGO_GIZ_URL = "/images/logogiz.png";
const LOGO_RIW_URL = "/images/logoriw.png";

export const Navigation: React.FC<NavigationProps> = ({
  setIsCheckBookingOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [activeLogoIdx, setActiveLogoIdx] = useState(0);

  // Rotating Logo Logic
  useEffect(() => {
    const logoTimer = setInterval(() => {
      setActiveLogoIdx((prev) => (prev === 0 ? 1 : 0));
    }, 4000);
    return () => clearInterval(logoTimer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (id: string | undefined) => {
    if (!id) return;
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const isHome = location.pathname === "/";

  return (
    <>
      <nav
        className={`fixed w-full z-[100] h-16 md:h-24 transition-all duration-500 ${
          showStickyNav
            ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
            : isHome
            ? "bg-transparent"
            : "bg-white border-b border-slate-100"
        }`}
        aria-label="Navigasi Utama"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            <Link
              to="/"
              className="flex items-center gap-3 cursor-pointer group relative h-full py-2"
              aria-label="Kembali ke atas"
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <div className="relative w-[100px] md:w-[150px] aspect-[3/2] overflow-hidden">
                <img
                  src={LOGO_GIZ_URL}
                  alt="GIZ Park Logo"
                  className={`absolute inset-0 w-full h-full object-contain transition-all duration-1000 transform ${
                    activeLogoIdx === 0
                      ? "opacity-100 scale-100 translate-y-0"
                      : "opacity-0 scale-90 translate-y-4"
                  }`}
                  loading="lazy"
                />
                <img
                  src={LOGO_RIW_URL}
                  alt="RIW Logo"
                  className={`absolute inset-0 w-full h-full object-contain transition-all duration-1000 transform ${
                    activeLogoIdx === 1
                      ? "opacity-100 scale-100 translate-y-0"
                      : "opacity-0 scale-90 translate-y-4"
                  }`}
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col">
                <span
                  className={`font-black text-xs md:text-xl leading-tight tracking-tight uppercase transition-colors duration-500 ${
                    showStickyNav || !isHome
                      ? "text-slate-900"
                      : "text-white drop-shadow-lg"
                  }`}
                >
                  gizrabbitland.com
                </span>
                <span
                  className={`font-bold text-[8px] md:text-[10px] uppercase tracking-[0.25em] transition-colors duration-500 ${
                    showStickyNav || !isHome
                      ? "text-indigo-600"
                      : "text-white/80"
                  }`}
                >
                  Official Tiket Booking
                </span>
              </div>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <NavItems
                setIsCheckBookingOpen={setIsCheckBookingOpen}
                showStickyNav={showStickyNav}
              />
            </div>

            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Buka Menu Navigasi"
              className={`md:hidden p-2 rounded-xl transition-colors active:scale-90 ${
                showStickyNav || !isHome
                  ? "text-slate-700 hover:bg-slate-50"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <MoreVertical className="w-8 h-8" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[150] bg-slate-900/60 backdrop-blur-md md:hidden transition-opacity duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`absolute right-0 top-0 h-full w-4/5 max-sm bg-white shadow-2xl transition-transform duration-500 ease-out p-8 flex flex-col ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-4 mb-12">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_GIZ_URL}
                alt="GIZ"
                className="h-10 w-auto object-contain bg-slate-50 p-1 rounded-lg border border-slate-100"
                loading="lazy"
              />
              <img
                src={LOGO_RIW_URL}
                alt="RIW"
                className="h-10 w-auto object-contain bg-slate-50 p-1 rounded-lg border border-slate-100"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-slate-900 text-lg uppercase tracking-tighter">
                gizrabbitland.com
              </span>
              <span className="font-bold text-indigo-600 text-[10px] uppercase tracking-[0.2em]">
                Official Tiket Booking
              </span>
            </div>
          </div>

          <div className="space-y-4 flex flex-col items-start px-2">
            <NavItems
              setIsCheckBookingOpen={setIsCheckBookingOpen}
              onLinkClick={() => setMobileMenuOpen(false)}
              showStickyNav={true}
            />
          </div>

          <div className="mt-auto pt-10 border-t border-slate-100 space-y-4">
            <button
              onClick={() => scrollToSection("book-section")}
              className="w-full py-5 bg-giz-primary text-white rounded-3xl font-black text-xs uppercase tracking-[0.25em] shadow-2xl shadow-indigo-100 flex items-center justify-center gap-3"
            >
              <Ticket className="w-5 h-5" /> Pesan Tiket Online
            </button>
            <p className="text-[10px] text-slate-400 font-bold uppercase text-center tracking-[0.3em]">
              gizrabbitland.com &copy; 2025
            </p>
            <a
              href="https://publish.co.id/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-[9px] text-indigo-500 font-bold uppercase text-center tracking-[0.2em] hover:text-indigo-600 transition-colors"
            >
              Publish Agency
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
